import { useCallback, useRef, useState } from "react";

export interface ChatDraft {
  content: string;
  documentIds: string[];
  viewIds: string[];
}
const empty = (): ChatDraft => ({ content: "", documentIds: [], viewIds: [] });
export function chatDraftKey(
  userId: string,
  projectId: string,
  conversationId: string | null,
) {
  return `taskflow:ai:draft:${userId}:${projectId || "personal"}:${conversationId || "new"}`;
}
function readDraft(key: string): ChatDraft {
  try {
    const value = JSON.parse(localStorage.getItem(key) || "null");
    return value && typeof value.content === "string"
      ? {
          content: value.content,
          documentIds: Array.isArray(value.documentIds)
            ? value.documentIds.filter((id: unknown) => typeof id === "string")
            : [],
          viewIds: Array.isArray(value.viewIds)
            ? value.viewIds.filter((id: unknown) => typeof id === "string")
            : [],
        }
      : empty();
  } catch {
    return empty();
  }
}
export function saveChatDraft(key: string, value: ChatDraft) {
  try {
    if (value.content || value.documentIds.length || value.viewIds.length)
      localStorage.setItem(key, JSON.stringify(value));
    else localStorage.removeItem(key);
  } catch {
    /* The composer remains usable when browser storage is unavailable. */
  }
}
export function useChatDraft(key: string) {
  const [cache, setCache] = useState(() => ({ key, value: readDraft(key) }));
  const latest = useRef(cache);
  const value = cache.key === key ? cache.value : readDraft(key);
  const update = useCallback(
    (
      patch: Partial<ChatDraft> | ((previous: ChatDraft) => Partial<ChatDraft>),
      targetKey = key,
    ) => {
      const previous =
        targetKey === latest.current.key
          ? latest.current.value
          : readDraft(targetKey);
      const next = {
        ...previous,
        ...(typeof patch === "function" ? patch(previous) : patch),
      };
      saveChatDraft(targetKey, next);
      latest.current = { key: targetKey, value: next };
      setCache({ key: targetKey, value: next });
    },
    [key],
  );
  return { ...value, update };
}
